
import {
    ActionResponse,
    ActionStatusHelper,
    DomainTableConfig,
    useAppEnum,
    useLayoutLifeCycle,
} from '@/Shared';

import { PropertyHeader } from '../property-config';
import { usePropertyEnricher } from '../Hooks';
import { DOMAIN_CONFIGS, DomainHookProps } from '@/Shared/Config/domain-config';
import { useDocument, useProperty } from '@/OneLiners';
import { PropertyViewModel } from '../ViewModels/PropertyViewModel';
import { PropertyModel } from '../Models';

/**
 * usePropertyTableConfig hook
 *
 * Domain-specific layout composition hook for Property entities.
 * Acts as the composition layer between:
 * - property and document domain data sources,
 * - enrichment logic,
 * - permission state resolution,
 * - and the generic layout lifecycle infrastructure.
 *
 * Responsibilities:
 * - Combine all required domain readiness states into a single contract.
 * - Provide dynamic header configuration for Property tables.
 * - Define the strict Model → ViewModel transformation.
 * - Delegate lifecycle orchestration to useLayoutLifeCycle.
 *
 * This hook is intentionally declarative.
 * It describes domain requirements while lifecycle mechanics
 * remain centralized in infrastructure.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 *
 * @see DomainHookProps
 * @see DomainTableConfig<PropertyViewModel>
 *
 * @param props Domain-level configuration and permissions
 * @returns DomainTableConfig<PropertyViewModel>
 */
export function usePropertyTableConfig(props: DomainHookProps): DomainTableConfig<PropertyViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.Property;

    /**
     * Property-related enums exposed to the UI layer.
     */
    const { PropertyEnums } = useAppEnum();

    /**
     * Property domain entry point.
     */
    const propertyHook = useProperty();

    /**
     * Document domain dependency.
     */
    const { dataMap: documentMap, stateResponse: documentResponse } = useDocument();

    /**
     * Property enrichment layer.
     */
    const {
        sectorMap,
        cityMap,
        getStrictEnrichedProperty,
        enricherResponse,
    } = usePropertyEnricher();

    /**
     * Combined readiness response.
     *
     * The layout lifecycle will only initialize once
     * this contract reaches a succeeded state.
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            propertyHook.stateResponse.status,
            documentResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: propertyHook.stateResponse.error ??
            documentResponse.error ??
            enricherResponse.error
    };

    /**
     * Delegation to generic layout lifecycle.
     */
    const lifeCycleHook = useLayoutLifeCycle({
        response,
        domainType,
        props,
        entityHook: propertyHook,

        /**
         * Header configuration for Property table.
         */
        headers: PropertyHeader.build(
            sectorMap, 
            cityMap, 
            PropertyEnums
        ),

        /**
         * Strict Model → ViewModel transformation.
         */
        toViewModelFn: (
            property: PropertyModel, isDisplaying: boolean) =>
            new PropertyViewModel({
                property: getStrictEnrichedProperty(property),
                viewEnums: PropertyEnums,
                documentMap,
                accessRight,
                isDisplaying
            })
    });

    return lifeCycleHook;
}